// Server-side analytics tracker for clinician web
// Sends events to the backend API for user-wise behavior tracking
import { http } from "../api/http";

let eventBuffer: any[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;
const FLUSH_INTERVAL = 10000;
const MAX_BUFFER_SIZE = 20;

/**
 * Get current user info from localStorage JWT
 */
const getUserInfo = (): { user_id: number; user_type: string } | null => {
    try {
        const token = localStorage.getItem("token");
        if (!token) return null;
        const payload = JSON.parse(atob(token.split(".")[1]));
        return {
            user_id: payload.sub || payload.userId || payload.id,
            user_type: "CLINICIAN",
        };
    } catch {
        return null;
    }
};

/**
 * Get or create a browser session ID
 */
const getSessionId = (): string => {
    let sessionId = sessionStorage.getItem("analytics_session_id");
    if (!sessionId) {
        sessionId = `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        sessionStorage.setItem("analytics_session_id", sessionId);
    }
    return sessionId;
};

/**
 * Track an event (buffered for batch sending)
 */
export const trackServerEvent = (eventName: string, params: Record<string, any> = {}): void => {
    const userInfo = getUserInfo();
    if (!userInfo) return;

    eventBuffer.push({
        event_name: eventName,
        platform: "clinician_web",
        page_path: window.location.pathname,
        session_id: getSessionId(),
        event_data: params,
    });

    if (eventBuffer.length >= MAX_BUFFER_SIZE) {
        flushEvents();
    }

    if (!flushTimer) {
        flushTimer = setInterval(flushEvents, FLUSH_INTERVAL);
    }
};

/**
 * Flush buffered events to the server
 */
const flushEvents = async (): Promise<void> => {
    if (eventBuffer.length === 0) return;

    const userInfo = getUserInfo();
    if (!userInfo) return;

    const events = [...eventBuffer];
    eventBuffer = [];

    try {
        await http.post("/analytics/track/batch", {
            ...userInfo,
            events,
        });
    } catch {
        eventBuffer = [...events.slice(-10), ...eventBuffer].slice(0, MAX_BUFFER_SIZE);
    }
};

// Flush before page unload
if (typeof window !== "undefined") {
    window.addEventListener("beforeunload", flushEvents);
    window.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") {
            flushEvents();
        }
    });
}
